import { createI18n } from "vue-i18n";
import { toRaw } from "vue";
import zh from "./zh-CN";
import en from "./en-US";
import ko from "./ko-KR";
import zhTW from "./zh-TW";
import ja from "./ja-JP";
import th from "./th-TH";
import vi from "./vi-VN";
import de from "./de-DE";
import fr from "./fr-FR";
import ru from "./ru-RU";
import es from "./es-ES";
import pt from "./pt-PT";
import it from "./it-IT";
import tr from "./tr-TR";
import pl from "./pl-PL";
import id from "./id-ID";
import ar from "./ar-SA";

const DEFAULT_LANGUAGE = "en-US";

const i18n = createI18n({
  legacy: false, // 使用 Composition API 模式
  locale: DEFAULT_LANGUAGE, // 临时默认语言，后续会从 API 获取后更新
  fallbackLocale: DEFAULT_LANGUAGE, // 备用语言
  messages: {
    "zh-CN": zh,
    "en-US": en,
    "ko-KR": ko,
    "zh-TW": zhTW,
    "ja-JP": ja,
    "th-TH": th,
    "vi-VN": vi,
    "de-DE": de,
    "fr-FR": fr,
    "ru-RU": ru,
    "es-ES": es,
    "pt-PT": pt,
    "it-IT": it,
    "tr-TR": tr,
    "pl-PL": pl,
    "id-ID": id,
    "ar-SA": ar,
  },
});

// RTL 语言列表
const RTL_LANGUAGES = ["ar-SA", "ar"];

// 判断是否为 RTL 语言
export const isRtlLanguage = (locale: string): boolean => {
  return RTL_LANGUAGES.some((rtlLang) =>
    locale.startsWith(rtlLang.split("-")[0])
  );
};

// 设置 HTML 文档的文本方向
export const setDocumentDirection = (locale: string) => {
  if (typeof document !== "undefined") {
    const htmlElement = document.documentElement;
    const dir = isRtlLanguage(locale) ? "rtl" : "ltr";
    htmlElement.setAttribute("dir", dir);
    htmlElement.setAttribute("lang", locale);
  }
};

// 设置 i18n 的语言
export const setI18nLocale = (locale: string) => {
  i18n.global.locale.value = locale;
  setDocumentDirection(locale);
};

export const getI18nLocale = () => {
  return i18n.global.locale.value;
};

// 保存原始的 t 方法
const originalT = i18n.global.t.bind(i18n.global);

// 存储 languageStore 的引用（在 store 初始化后设置）
let languageStoreInstance: any = null;

/**
 * 设置 languageStore 实例（在 language store 初始化后调用）
 * @param store - languageStore 实例
 */
export const setLanguageStore = (store: any) => {
  languageStoreInstance = store;
};

/**
 * 从 languagePack 中获取翻译
 * @param key - 翻译键，如 "avatar.title"
 * @param locale - 当前语言代码
 * @param languagePack - 后台语言包
 * @returns 翻译文本，如果不存在则返回 undefined
 */
function getFromLanguagePack(
  key: string,
  locale: string,
  languagePack: Record<string, Record<string, string>>
): string | undefined {
  if (!languagePack.value || !locale.value) {
    return undefined;
  }

  // 获取当前语言的翻译包
  const currentLangPack = languagePack.value?.[locale.value];
  if (!currentLangPack) {
    return undefined;
  }

  // 直接从语言包中获取
  return currentLangPack[key];
}

/**
 * 自定义的 t 方法，优先从 languagePack 获取，如果没有则使用 i18n
 * @param key - 翻译键
 * @param params - 翻译参数（可选）
 * @param options - 翻译选项（可选）
 */
function customT(key: string, params?: any, options?: any): string {
  // 获取 languageStore
  let languagePack: Record<string, Record<string, string>> = {};
  let currentLocale: string = i18n.global.locale;

  if (languageStoreInstance) {
    // 使用 toRaw 获取原始值，避免响应式代理的问题
    const rawStore = toRaw(languageStoreInstance);
    languagePack = (rawStore.languagePack || {}) as Record<
      string,
      Record<string, string>
    >;
  } else {
    console.warn("languageStoreInstance is null");
  }

  // 优先从 languagePack 获取
  const packValue = getFromLanguagePack(key, currentLocale, languagePack);
  if (packValue !== undefined && packValue !== null && packValue !== "") {
    // 如果 languagePack 中有值，直接返回
    // 如果需要参数替换，可以在这里处理
    if (params && typeof packValue === "string") {
      // 简单的参数替换，支持 {key} 格式
      return packValue.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined
          ? String(params[paramKey])
          : match;
      });
    }
    return packValue;
  }

  // 如果 languagePack 中没有，使用原始的 i18n t 方法
  const i18nValue = originalT(key, params, options);

  // 如果没有匹配到翻译（返回的仍然是 key），则返回 key 的最后一部分
  if (i18nValue === key && key.includes(".")) {
    const lastDotIndex = key.lastIndexOf(".");
    return key.substring(lastDotIndex + 1);
  }

  return i18nValue;
}

// 覆盖 i18n.global.t 方法
i18n.global.t = customT as any;

export default i18n;
